import { Close as CloseIcon } from '@mui/icons-material';
import { Box, ClickAwayListener, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { type ColorResult, BlockPicker } from 'react-color';
import { useModal } from 'src/hooks';

import { hex8ToRgba } from '../ColorPickerWidget.helper';
import {
  ColorBar,
  ColorPickerCloseButton,
  IconWrapper,
  PickerWrapper,
  StyledSketchPicker,
} from '../ColorPickerWidget.styled';
import type { ColorPickerContentProps } from './ColorPickerContent.interface';

export const ColorPickerContent = (props: ColorPickerContentProps) => {
  const colorPickerModal = useModal();
  const [position, setPosition] = useState<{
    top: number | 'auto';
    bottom: string | 'auto';
    left: number;
  }>({ top: 0, bottom: 'auto', left: 0 });

  useEffect(() => {
    const handleScroll = () => {
      if (colorPickerModal.open) {
        colorPickerModal.onCloseModal();
      }
    };

    window.addEventListener('scroll', handleScroll, true); // true for capture phase

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [colorPickerModal]);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();

    // Get the actual click coordinates
    const clickX = event.clientX;
    const clickY = event.clientY;

    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const pickerWidth = 320; // Adjust based on your picker's width
    const pickerHeight = 400; // Adjust based on your picker's height
    const margin = 10;

    // Ensure the picker stays within viewport bounds
    const left = Math.min(clickX, windowWidth - pickerWidth - margin);
    const top = Math.min(clickY, windowHeight - pickerHeight - margin);

    setPosition({
      top: Math.max(margin, top),
      bottom: 'auto',
      left: Math.max(margin, left),
    });

    colorPickerModal.onOpenModal();
  };

  const pickerStyles = {
    default: {
      picker: {
        width: '250px',
        padding: '0 10px',
        boxShadow: 'initial',
      },
    },
  };

  const renderColorPicker = {
    block: (
      <BlockPicker
        colors={props.defaultColors}
        color={props.value}
        onChange={(color: ColorResult) => props.onChange(color)}
        triangle={props.triangle}
      />
    ),
    sketch: (
      <StyledSketchPicker
        styles={pickerStyles}
        color={hex8ToRgba(props.value)}
        onChange={(color: ColorResult) => props.onChange(color)}
      />
    ),
  };

  return (
    <ClickAwayListener onClickAway={colorPickerModal.onCloseModal}>
      <Box position="relative" width="fit-content">
        <IconWrapper
          {...(props.showBackgroundColor && {
            $backgroundColor: props.value ?? 'black',
          })}
          selected={colorPickerModal.open}
          $showBorder={props.showBorder}
          disabled={props.isViewOnly}
          disableRipple
          onClick={handleClick}>
          {!props.showBackgroundColor && (
            <>
              <Typography variant="h2">A</Typography>
              <ColorBar color={props.value} />
            </>
          )}
        </IconWrapper>

        {colorPickerModal.open && (
          <PickerWrapper
            $colorPickerMode={props.colorPickerMode}
            style={{
              ...props?.pickerWrapperStyle,
              position: 'fixed',
              ...position,
            }}>
            {props.colorPickerMode !== 'block' && (
              <ColorPickerCloseButton onClick={colorPickerModal.onCloseModal}>
                <CloseIcon />
              </ColorPickerCloseButton>
            )}

            {renderColorPicker[props.colorPickerMode ?? 'sketch']}
          </PickerWrapper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
